import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchFeatureFlags } from "../lib/api";

/**
 * Lightweight global feature-flag store. Polls `/api/feature-flags`
 * once on mount and exposes the result via `useFeatureFlags()`.
 * Every gated surface (Tip button, Wallet page, etc.) defaults to
 * OFF until the backend confirms the flag is enabled — so a slow
 * network or 5xx never accidentally reveals a hidden feature.
 */
const Ctx = createContext({ flags: { tips_enabled: false }, refresh: () => {} });

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState({ tips_enabled: false });

  const refresh = React.useCallback(async () => {
    try {
      const r = await fetchFeatureFlags();
      setFlags({ tips_enabled: !!r?.tips_enabled });
    } catch (_e) {
      // Fail closed — keep tips_enabled=false if the call fails.
    }
  }, []);

  useEffect(() => {
    refresh();
    // Re-fetch when the tab regains focus so an admin who flips
    // `tips_enabled` in one tab sees the change reflect in their
    // other open tabs without a hard refresh. Cheap enough to run
    // every focus event because the endpoint is unauthenticated and
    // returns a 2-byte JSON blob.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  return <Ctx.Provider value={{ flags, refresh }}>{children}</Ctx.Provider>;
}

export const useFeatureFlags = () => useContext(Ctx);
